import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  InputLabel,
  Button,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Switch,
  Box,
  Slider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CalculateIcon from '@mui/icons-material/Calculate';
import CloseIcon from '@mui/icons-material/Close';
import NumberFormatTextField from '../../../../../components/NumberFormatTextField/NumberFormatTextField';
import MySlider from '../../../../../components/MySlider/MySlider';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { addThousandSeparators } from '../../../../../utils/formatNumber';
import _ from 'lodash';

const DEFAULT_MIN_LOAN_INTEREST = 2;
const DEFAULT_MAX_LOAN_INTEREST = 8;
const FULL_MIN_LOAN_INTEREST = 0.25;
const FULL_MAX_LOAN_INTEREST = 10;
const LOAN_INTEREST_STEP = 0.125;

export const sanitizeLoanInterest = value => {
  const numericValue = parseFloat(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_MIN_LOAN_INTEREST;
  }
  return Math.max(FULL_MIN_LOAN_INTEREST, Math.min(FULL_MAX_LOAN_INTEREST, numericValue));
};

const formatLoanInterestInputValue = value => (value === undefined ? '' : value === 0 ? '0.' : value);

const isAllowedLoanInterest = values => {
  const { formattedValue, value } = values;
  const numericValue = parseFloat(value);
  // Allow 0 / 0.x while typing so 0.25% can be entered; sanitize clamps on blur.
  return formattedValue === '' || (numericValue >= 0 && numericValue <= FULL_MAX_LOAN_INTEREST);
};

const isOutsideDefaultLoanInterestRange = rate => {
  const numericValue = parseFloat(rate);
  return (
    Number.isFinite(numericValue) &&
    (numericValue < DEFAULT_MIN_LOAN_INTEREST || numericValue > DEFAULT_MAX_LOAN_INTEREST)
  );
};

const getLoanInterestSelectorRange = rates => {
  const list = Array.isArray(rates) ? rates : [rates];
  if (list.some(isOutsideDefaultLoanInterestRange)) {
    return { min: FULL_MIN_LOAN_INTEREST, max: FULL_MAX_LOAN_INTEREST };
  }
  return { min: DEFAULT_MIN_LOAN_INTEREST, max: DEFAULT_MAX_LOAN_INTEREST };
};

const CustomizeInterestRow = ({
  termLoanPercentage,
  termAddOn,
  loan,
  i,
  intl,
  handleInputBankTermInterest,
  handleInputBankTermInterestOnBlur,
  expanded,
  onToggleExpanded,
  overrideLoanInterestSliderValue,
  isCurrencyEnabled
}) => {
  return (
    <>
      <TableRow>
        <TableCell>{i + 1}</TableCell>
        <TableCell className="loan-percentage">
          {
            <NumberFormatTextField
              // disabled={isCurrencyEnabled}
              name="termLoanPercentage"
              value={formatLoanInterestInputValue(termLoanPercentage[i])}
              onChange={event => handleInputBankTermInterest(event, i)}
              onBlur={() => handleInputBankTermInterestOnBlur(i)}
              inputProps={{
                allowNegative: false,
                suffix: '%',
                decimalScale: 3,
                isAllowed: isAllowedLoanInterest
              }}
              endAdornment={
                <IconButton aria-label="expand row" size="small" onClick={onToggleExpanded}>
                  {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              }
            />
          }
        </TableCell>
        <TableCell className="term-add-on">
          {
            <NumberFormatTextField
              disabled={isCurrencyEnabled}
              name="termAddOn"
              value={termAddOn[i]}
              onChange={isCurrencyEnabled ? undefined : event => handleInputBankTermInterest(event, i)}
              inputProps={{
                style: {
                  textAlign: 'center'
                },
                allowNegative: true,
                prefix: '$',
                decimalScale: 0,
                isAllowed: values => {
                  const { formattedValue, value } = values;
                  return formattedValue === '' || value || value === 0;
                }
              }}
            />
          }
        </TableCell>
      </TableRow>
      <TableRow className="collapse-rows">
        <TableCell />
        <TableCell colSpan={2}>
          <Collapse in={expanded}>
            {intl.formatMessage(
              { id: 'pfa-table-mthly-interest' },
              {
                interest: addThousandSeparators(
                  ((loan * sanitizeLoanInterest(termLoanPercentage[i])) / (12 * 100)).toFixed(0)
                )
              }
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Map an SVG-space X coordinate to the nearest pin index. Per-pin invisible hit
// circles overlapped ~3-deep at mobile size (16 pins ~21px apart, 64px targets),
// so a tap almost always grabbed the wrong (topmost) pin. Picking the nearest pin
// by X removes that ambiguity and makes every pin reachable. Exported for testing.
export const nearestPinIndex = (svgX, dims, n) => {
  if (!n || n < 1) return null;
  if (n === 1) return 0;
  const { W, padL, padR } = dims;
  const chartW = W - padL - padR;
  const i = Math.round(((svgX - padL) / chartW) * (n - 1));
  return Math.max(0, Math.min(n - 1, i));
};

export default class SideLoanInterestInput extends React.PureComponent {
  state = { equalizerOpen: false, draggingIndex: null, hoverIndex: null, overrideLoanInterestInputValue: null };
  svgRef = React.createRef();
  // Drag is tracked synchronously (setState is async) so the non-passive
  // touchmove listener can already suppress scrolling on the first move.
  isDragging = false;
  chartWrapperNode = null;

  componentDidUpdate(prevProps) {
    const yearlyRatesWereBulkUpdated =
      prevProps.termLoanPercentage !== this.props.termLoanPercentage &&
      Array.isArray(this.props.termLoanPercentage) &&
      this.props.termLoanPercentage.every(rate => rate === this.props.bankLoanInterest);

    if (
      (prevProps.bankLoanInterest !== this.props.bankLoanInterest || yearlyRatesWereBulkUpdated) &&
      this.state.overrideLoanInterestInputValue !== null
    ) {
      this.setState({ overrideLoanInterestInputValue: null });
    }
  }

  // WebKit (iOS Safari) ignores `touch-action` on SVG elements, so a vertical pin
  // drag is treated as a page scroll and the browser fires `pointercancel`, killing
  // the drag. We attach a *non-passive* touchmove listener to the HTML wrapper
  // (React 17 registers synthetic touch listeners as passive, so preventDefault in
  // a React handler is a no-op) and cancel the scroll there while dragging.
  handleChartWrapperRef = node => {
    if (this.chartWrapperNode) {
      this.chartWrapperNode.removeEventListener('touchmove', this.preventTouchScrollWhileDragging);
    }
    this.chartWrapperNode = node;
    if (node) {
      node.addEventListener('touchmove', this.preventTouchScrollWhileDragging, { passive: false });
    }
  };

  preventTouchScrollWhileDragging = e => {
    if (this.isDragging && e.cancelable) e.preventDefault();
  };

  handleSlideBankLoanInterest = (event, value) =>
    this.props.handleChange({
      target: {
        name: 'bankLoanInterest',
        value: value
      }
    });

  handleSlideBankTermLoanInterest = (event, value, index) => {
    const newPercentages = [...this.props.termLoanPercentage];
    newPercentages[index] = value;
    this.props.handleChange({
      target: {
        name: 'termLoanPercentage',
        value: newPercentages
      }
    });
  };

  handleSlideOverrideLoanInterestSliderValue = (event, value) => {
    if (this.state.overrideLoanInterestInputValue !== null) {
      this.setState({ overrideLoanInterestInputValue: null });
    }

    this.props.handleChange({
      target: {
        name: 'overrideLoanInterestSliderValue',
        value: value
      }
    });
  };

  handleInputOverrideLoanInterest = event => {
    this.setState({ overrideLoanInterestInputValue: event.target.value === undefined ? '' : event.target.value });
  };

  commitInputOverrideLoanInterest = () => {
    const { overrideLoanInterestInputValue } = this.state;
    if (overrideLoanInterestInputValue === null) {
      return;
    }

    const value = sanitizeLoanInterest(overrideLoanInterestInputValue);
    this.setState({ overrideLoanInterestInputValue: null });
    this.handleSlideOverrideLoanInterestSliderValue(null, value);
  };

  handleInputOverrideLoanInterestKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitInputOverrideLoanInterest();
    }
  };

  handleInputBankTermInterest = (event, index) => {
    const { termLoanPercentage, termAddOn } = this.props;
    const newPercentages = [...(event.target.name === 'termLoanPercentage' ? termLoanPercentage : termAddOn)];
    newPercentages[index] = event.target.value;
    this.props.handleChange({
      target: {
        name: event.target.name,
        value: newPercentages
      }
    });
  };

  handleInputBankTermInterestOnBlur = index => {
    const e = {
      target: {
        value: sanitizeLoanInterest(this.props.termLoanPercentage[index]),
        name: 'termLoanPercentage'
      }
    };
    this.handleInputBankTermInterest(e, index);
  };

  handleLoanInterestOnBlur = () => {
    this.handleSlideBankLoanInterest(null, sanitizeLoanInterest(this.props.bankLoanInterest));
  };

  // ── Wave equalizer chart ──────────────────────────────────────────────────

  /** Catmull-Rom → cubic Bézier: smooth SVG path through all points. */
  getWavePath = pts => {
    if (pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(0, i - 1)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(pts.length - 1, i + 2)];
      const t = 0.25;
      const cp1x = p1.x + (p2.x - p0.x) * t;
      const cp1y = p1.y + (p2.y - p0.y) * t;
      const cp2x = p2.x - (p3.x - p1.x) * t;
      const cp2y = p2.y - (p3.y - p1.y) * t;
      d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x},${p2.y}`;
    }
    return d;
  };

  // Chart geometry. The viewBox is scaled to the container width (width:100%),
  // so on a phone the desktop 820-wide box shrinks the pins to a few px and the
  // short 270 height leaves almost no vertical drag range. Use a narrower, taller
  // box with bigger pins/touch targets on small screens so it stays usable.
  getChartDims = () => {
    return this.props.isSmallScreen
      ? {
          W: 380,
          H: 380,
          padT: 58,
          padB: 48,
          padL: 40,
          padR: 20,
          pinR: 13,
          pinRActive: 16,
          glowR: 24,
          tipW: 66,
          tipH: 26,
          tipFont: 13,
          axisFont: 12,
          yearFont: 12
        }
      : {
          W: 820,
          H: 270,
          padT: 54,
          padB: 40,
          padL: 36,
          padR: 16,
          pinR: 9,
          pinRActive: 11,
          glowR: 17,
          tipW: 58,
          tipH: 22,
          tipFont: 11,
          axisFont: 10,
          yearFont: 10
        };
  };

  // Pointer Events unify mouse + touch + pen, so the wave equalizer is draggable
  // on phones — the old onMouse* handlers never fired for touch, so the chart was
  // dead on mobile. `touch-action: none` on the <svg> stops the page from
  // scrolling/zooming mid-drag, and pointer capture keeps the drag alive even when
  // the finger strays off the pin / outside the small chart.
  // Nearest pin to the pointer's X (handled at the <svg> level, not per-pin).
  pinIndexFromEvent = e => {
    const svg = this.svgRef.current;
    if (!svg) return null;
    const n = (this.props.termLoanPercentage || []).length;
    const { W, padL, padR } = this.getChartDims();
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    return nearestPinIndex(svgX, { W, padL, padR }, n);
  };

  // Snapped, clamped interest-rate value from the pointer's Y.
  valueFromEvent = e => {
    const svg = this.svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const { H, padT, padB } = this.getChartDims();
    const { min, max } = getLoanInterestSelectorRange(this.props.termLoanPercentage);
    const step = LOAN_INTEREST_STEP;
    const svgY = ((e.clientY - rect.top) / rect.height) * H;
    const ratio = 1 - (svgY - padT) / (H - padT - padB);
    const raw = min + ratio * (max - min);
    const snapped = Math.round(raw / step) * step;
    return Math.max(min, Math.min(max, snapped));
  };

  handleWavePointerDown = e => {
    const idx = this.pinIndexFromEvent(e);
    if (idx === null) return;
    e.preventDefault();
    this.isDragging = true;
    const svg = this.svgRef.current;
    if (svg && svg.setPointerCapture) {
      try {
        svg.setPointerCapture(e.pointerId);
      } catch (err) {}
    }
    this.setState({ draggingIndex: idx });
    const value = this.valueFromEvent(e);
    if (value !== null) this.handleSlideBankTermLoanInterest(null, value, idx);
  };

  handleWavePointerMove = e => {
    const { draggingIndex } = this.state;
    if (draggingIndex === null) {
      // Mouse hover (no button held) → preview the nearest pin's tooltip.
      if (e.pointerType === 'mouse') {
        const idx = this.pinIndexFromEvent(e);
        if (idx !== this.state.hoverIndex) this.setState({ hoverIndex: idx });
      }
      return;
    }
    const value = this.valueFromEvent(e);
    if (value !== null) this.handleSlideBankTermLoanInterest(null, value, draggingIndex);
  };

  handleWavePointerUp = e => {
    this.isDragging = false;
    const svg = this.svgRef.current;
    if (svg && svg.releasePointerCapture && e && e.pointerId != null) {
      try {
        svg.releasePointerCapture(e.pointerId);
      } catch (err) {}
    }
    this.setState({ draggingIndex: null });
  };

  renderWaveChart = () => {
    const { termLoanPercentage } = this.props;
    const { draggingIndex, hoverIndex } = this.state;
    const values = (termLoanPercentage || []).map(sanitizeLoanInterest);
    const n = values.length;
    if (n === 0) return null;

    const {
      W,
      H,
      padT,
      padB,
      padL,
      padR,
      pinR,
      pinRActive,
      glowR,
      tipW: TIP_W,
      tipH: TIP_H,
      tipFont,
      axisFont,
      yearFont
    } = this.getChartDims();
    const TIP_R = 5,
      ARROW = 6;
    const { min, max } = getLoanInterestSelectorRange(termLoanPercentage);
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;

    const xOf = i => padL + (n > 1 ? (i / (n - 1)) * chartW : chartW / 2);
    const yOf = v => padT + (1 - (v - min) / (max - min)) * chartH;

    const pts = values.map((v, i) => ({ x: xOf(i), y: yOf(v) }));
    const linePath = this.getWavePath(pts);
    const lastPt = pts[pts.length - 1];
    const firstPt = pts[0];
    const baseline = H - padB;
    const areaPath = `${linePath} L ${lastPt.x},${baseline} L ${firstPt.x},${baseline} Z`;

    const gridValues = min === FULL_MIN_LOAN_INTEREST ? [0.25, 2, 4, 6, 8, 10] : [2, 4, 6, 8];

    return (
      // HTML wrapper carries `touch-action: none` (WebKit honours it here but not
      // on the <svg>) and hosts the non-passive touchmove guard via ref.
      <div ref={this.handleChartWrapperRef} style={{ width: '100%', touchAction: 'none' }}>
        <svg
          ref={this.svgRef}
          viewBox={`0 0 ${W} ${H}`}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            userSelect: 'none',
            // Stop the page from scrolling/zooming while a pin is being dragged by touch.
            touchAction: 'none',
            cursor: draggingIndex !== null ? 'ns-resize' : 'default'
          }}
          onPointerDown={this.handleWavePointerDown}
          onPointerMove={this.handleWavePointerMove}
          onPointerUp={this.handleWavePointerUp}
          onPointerCancel={this.handleWavePointerUp}
          onMouseLeave={() => this.setState({ hoverIndex: null })}
        >
          {/* Transparent backdrop so a press anywhere in the chart starts a drag. */}
          <rect x={0} y={0} width={W} height={H} fill="transparent" />
          {/* Y-axis grid */}
          {gridValues.map(v => (
            <g key={v}>
              <line x1={padL} y1={yOf(v)} x2={W - padR} y2={yOf(v)} stroke="#f0f0f0" strokeWidth="1" />
              <text x={padL - 5} y={yOf(v) + 4} textAnchor="end" fontSize={axisFont} fill="#bbb">
                {v}%
              </text>
            </g>
          ))}

          {/* X-axis baseline */}
          <line x1={padL} y1={baseline} x2={W - padR} y2={baseline} stroke="#e8e8e8" strokeWidth="1" />

          {/* Filled area under the wave */}
          <path d={areaPath} fill="rgba(102, 94, 255, 0.08)" />

          {/* Wave line */}
          <path
            d={linePath}
            fill="none"
            stroke="#665EFF"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Pins */}
          {pts.map((p, i) => {
            const isActive = draggingIndex === i || hoverIndex === i;
            const r = isActive ? pinRActive : pinR;
            const label = `${values[i].toFixed(3)}%`;
            // Keep tooltip box inside SVG horizontally
            const tipX = Math.max(padL, Math.min(W - padR - TIP_W, p.x - TIP_W / 2));
            const tipY = p.y - r - ARROW - TIP_H;
            return (
              // Pins are visual only; the drag is handled at the <svg> level
              // (nearest pin by X), so per-pin hit targets no longer overlap.
              <g key={i} style={{ cursor: 'ns-resize', pointerEvents: 'none' }}>
                {/* Glow ring when active */}
                {isActive && <circle cx={p.x} cy={p.y} r={glowR} fill="rgba(102,94,255,0.12)" />}
                {/* Pin body */}
                <circle cx={p.x} cy={p.y} r={r} fill="#665EFF" />
                <circle cx={p.x} cy={p.y} r={r - 4} fill="white" />

                {/* Tooltip callout — shown on hover or drag */}
                {isActive && (
                  <g pointerEvents="none">
                    <rect x={tipX} y={tipY} width={TIP_W} height={TIP_H} rx={TIP_R} fill="#665EFF" />
                    {/* Down-pointing arrow */}
                    <polygon
                      points={`${p.x - 5},${tipY + TIP_H} ${p.x + 5},${tipY + TIP_H} ${p.x},${tipY + TIP_H + ARROW}`}
                      fill="#665EFF"
                    />
                    <text
                      x={tipX + TIP_W / 2}
                      y={tipY + TIP_H - 6}
                      textAnchor="middle"
                      fontSize={tipFont}
                      fill="white"
                      fontWeight="700"
                    >
                      {label}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Year labels */}
          {values.map((_, i) => (
            <text key={i} x={xOf(i)} y={baseline + 16} textAnchor="middle" fontSize={yearFont} fill="#999">
              {i + 1}
            </text>
          ))}

          {/* X-axis title */}
          <text x={W / 2} y={H - 2} textAnchor="middle" fontSize={axisFont + 1} fill="#aaa" fontWeight="500">
            年期
          </text>
        </svg>
      </div>
    );
  };

  render() {
    const {
      intl,
      bankLoanInterest,
      handleChange,
      overrideLoanInterest,
      termLoanPercentage,
      termAddOn,
      handleCheckedChange,
      loan,
      fixedLoanInterestExpanded,
      onToggleFixedLoanInterestExpanded,
      dynamicLoanInterestExpanded,
      onToggleDynamicLoanInterestExpanded,
      overrideLoanInterestSliderValue,
      template,
      isCurrencyEnabled,
      openLoanInterestCalculatorDialog
    } = this.props;
    const { overrideLoanInterestInputValue } = this.state;
    const sliderValue = (overrideLoanInterest ? overrideLoanInterestSliderValue : bankLoanInterest) ?? 0;
    const { min: sliderMin, max: sliderMax } = getLoanInterestSelectorRange(
      overrideLoanInterest ? termLoanPercentage : sliderValue
    );
    return (
      <Card className="pfa-card pf-card-container pf-loan-interest-input-panel">
        <CardContent>
          <Grid container spacing={1}>
            {!template && (
              <Grid size={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                  }}
                >
                  <FormControlLabel
                    sx={{ mr: 0, minWidth: 0 }}
                    control={
                      <Switch
                        // disabled={isCurrencyEnabled}
                        checked={!overrideLoanInterest}
                        onChange={handleCheckedChange}
                        name="overrideLoanInterest"
                        color="primary"
                      />
                    }
                    label={intl.formatMessage({ id: 'custom-loan-interest' })}
                  />
                  {openLoanInterestCalculatorDialog && (
                    <Tooltip title={intl.formatMessage({ id: 'pfa-loan-interest-cal-dialog-title' })}>
                      <span>
                        <IconButton
                          color="primary"
                          size="small"
                          aria-label={intl.formatMessage({ id: 'pfa-loan-interest-cal-dialog-title' })}
                          onClick={openLoanInterestCalculatorDialog}
                          sx={{ p: 0.5 }}
                        >
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            )}
            {template && <Box style={{ height: '12px', width: '20px' }} />}
            <Grid size={12} className="bank-loan-interest slider-control">
              <Grid container style={{ marginBottom: '13px' }}>
                <Grid size={6} style={{ display: 'flex', alignItems: 'center' }}>
                  <InputLabel>{intl.formatMessage({ id: 'pfa-bank-loan-interest' })}</InputLabel>
                </Grid>
                {!overrideLoanInterest ? (
                  <Grid size={6}>
                    <NumberFormatTextField
                      // disabled={isCurrencyEnabled}
                      name="bankLoanInterest"
                      value={bankLoanInterest === 0 ? '0.' : bankLoanInterest}
                      onChange={handleChange}
                      onBlur={this.handleLoanInterestOnBlur}
                      inputProps={{
                        allowNegative: false,
                        suffix: '%',
                        decimalScale: 3,
                        isAllowed: isAllowedLoanInterest
                      }}
                      endAdornment={
                        <IconButton aria-label="expand row" size="small" onClick={onToggleFixedLoanInterestExpanded}>
                          {fixedLoanInterestExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      }
                    />
                  </Grid>
                ) : (
                  <Grid size={6}>
                    <NumberFormatTextField
                      name="bankLoanInterest"
                      value={
                        overrideLoanInterestInputValue === null
                          ? formatLoanInterestInputValue(bankLoanInterest)
                          : formatLoanInterestInputValue(overrideLoanInterestInputValue)
                      }
                      onChange={this.handleInputOverrideLoanInterest}
                      onBlur={this.commitInputOverrideLoanInterest}
                      onKeyDown={this.handleInputOverrideLoanInterestKeyDown}
                      inputProps={{
                        allowNegative: false,
                        suffix: '%',
                        decimalScale: 3,
                        isAllowed: isAllowedLoanInterest
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              {!overrideLoanInterest && (
                <Collapse in={fixedLoanInterestExpanded} className="mthly-interest">
                  {intl.formatMessage(
                    { id: 'pfa-table-mthly-interest' },
                    { interest: addThousandSeparators(((loan * (bankLoanInterest ?? 0)) / (12 * 100)).toFixed(0)) }
                  )}
                </Collapse>
              )}

              <div className="labelled-slider">
                <Grid
                  container
                  style={{ marginBottom: overrideLoanInterest ? 12 : 0 }}
                  sx={{
                    alignItems: 'center'
                  }}
                >
                  <Grid size={12}>
                    <MySlider
                      // disabled={isCurrencyEnabled}
                      min={sliderMin}
                      max={sliderMax}
                      step={LOAN_INTEREST_STEP}
                      value={sliderValue}
                      onChange={overrideLoanInterest ? this.handleSlideOverrideLoanInterestSliderValue : undefined}
                      onChangeCommitted={
                        overrideLoanInterest
                          ? this.handleSlideOverrideLoanInterestSliderValue
                          : this.handleSlideBankLoanInterest
                      }
                    />
                  </Grid>
                </Grid>
                {overrideLoanInterest && (
                  <>
                    {/* Equalizer button — interactive control, omit from the PDF export. */}
                    {!template && (
                      <Box style={{ marginBottom: 8, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            <ShowChartIcon style={{ fontSize: 20, transition: 'transform 0.2s ease-in-out' }} />
                          }
                          onClick={() => this.setState({ equalizerOpen: true })}
                          sx={{
                            color: '#665EFF',
                            borderColor: '#665EFF',
                            borderRadius: '20px',
                            padding: '4px 16px',
                            textTransform: 'none',
                            fontSize: 14,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 94, 255, 0.08)',
                              borderColor: '#665EFF',
                              transform: 'scale(1.02)'
                            },
                            '&:hover svg': {
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          {intl.formatMessage({ id: 'input-using-equalizer' })}
                        </Button>
                      </Box>
                    )}

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{intl.formatMessage({ id: 'Year' })}</TableCell>
                            <TableCell>{intl.formatMessage({ id: 'pfa-bank-loan-interest' })}</TableCell>
                            <TableCell>{intl.formatMessage({ id: 'pfa-fixed-cost' })}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {termLoanPercentage.map((e, i) => {
                            return (
                              <CustomizeInterestRow
                                key={`bank-loan-${i}`}
                                termLoanPercentage={termLoanPercentage}
                                termAddOn={termAddOn}
                                i={i}
                                loan={loan}
                                intl={intl}
                                handleInputBankTermInterest={this.handleInputBankTermInterest}
                                expanded={_.get(dynamicLoanInterestExpanded, `[${i}]`, false)}
                                onToggleExpanded={() => onToggleDynamicLoanInterestExpanded(i)}
                                handleInputBankTermInterestOnBlur={this.handleInputBankTermInterestOnBlur}
                                isCurrencyEnabled={isCurrencyEnabled}
                              />
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </CardContent>
        {/* Equalizer Dialog – wave chart */}
        <Dialog
          open={this.state.equalizerOpen}
          onClose={() => this.setState({ equalizerOpen: false })}
          maxWidth="md"
          fullWidth
          // On a phone, go full-screen so the wave chart has the whole viewport to
          // render at a usable size instead of a cramped ~280px-wide box.
          fullScreen={!!this.props.isSmallScreen}
        >
          <DialogTitle>
            {intl.formatMessage({ id: 'pfa-bank-loan-interest' })}
            <IconButton
              aria-label="close"
              onClick={() => this.setState({ equalizerOpen: false })}
              // Offset by the iOS safe-area inset so the button clears the status bar
              // when the dialog is full-screen on a notched iPhone (0 on desktop).
              style={{ position: 'absolute', right: 8, top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: '8px 24px 24px' }}>{this.renderWaveChart()}</DialogContent>
        </Dialog>
      </Card>
    );
  }
}
